import { useEffect, useState } from "react";import { useParams, useNavigate, Link } from "react-router-dom";
import { songWrapSlug } from "@/lib/wrapLinks";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Disc3, ChevronRight, Share2, Link2 } from "lucide-react";
import api, { thumbUrl } from "@/lib/api";
import { CoverSetter } from "@/components/CoverSetter";
import { TopBar } from "@/components/TopBar";
import { PlatformBadge } from "@/components/PlatformBadge";
import { CampaignReport } from "@/components/CampaignReport";
import { ImportLinkDialog } from "@/components/dialogs/ImportLinkDialog";
import { PressDialog } from "@/components/dialogs/PressDialog";
import { ShareDialog } from "@/components/dialogs/ShareDialog";
import { PressSection } from "@/components/PressSection";
import { EditorialPlacements } from "@/components/EditorialPlacements";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtFull } from "@/lib/format";
import { DASH_ZERO_SONG_ID } from "@/lib/dashZeros";

export default function SongDashboard() {
  const { songId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [pressOpen, setPressOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = () => api.get(`/songs/${songId}`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load song"));
  useEffect(() => { load(); }, [songId]);

  const song = data?.song;
  const artist = data?.artist;
  const campaigns = data?.campaigns || [];

  const deletePress = async (p) => {
    try {
      await api.delete(`/press/${p.id}`);
      toast.success("Press removed");
      load();
    } catch { toast.error("Couldn't remove press"); }
  };

  const runsSlot = (
    <div className="mt-10">
      <h2 className="mb-4 font-display text-xl font-bold">Campaign runs <span className="text-neutral-400">({campaigns.length})</span></h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {campaigns.map((c) => (
          <button
            key={c.id}
            data-testid={`run-card-${c.id}`}
            onClick={() => navigate(`/campaigns/${c.id}`)}
            className="pb-card group flex items-center gap-4 p-4 text-left transition-transform hover:-translate-y-1"
          >
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-neutral-900">
              {c.cover_art ? <img src={thumbUrl(c.cover_art, 120)} alt="" width={40} height={40} loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-neutral-600"><Disc3 className="h-5 w-5" /></div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2"><PlatformBadge platform={c.platform} /></div>
              <p className="mt-1 truncate font-semibold">{c.title}</p>
              <p className="text-xs text-neutral-500">{c.summary.post_count} posts · {c.summary.total_views == null ? "—" : fmtFull(c.summary.total_views)} views</p>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-neutral-300 transition-transform group-hover:translate-x-1 group-hover:text-neutral-900" />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <TopBar
        right={
          <div className="flex items-center gap-3">
            {songWrapSlug(artist?.name, song?.title) && <Link data-testid="song-wrap-report-link" to={`/wraps/${songWrapSlug(artist?.name, song?.title)}`} className="text-sm font-medium text-[#35606E] hover:underline">Wrap report</Link>}
            {songWrapSlug(artist?.name, song?.title) === "immaculate" && <Link data-testid="song-wrap-sample-link" to="/wraps/immaculate#observed-sample" className="text-sm font-medium text-[#35606E] hover:underline">Observed sample</Link>}
            <Button data-testid="song-share-btn" onClick={() => setShareOpen(true)} variant="outline" className="rounded-full border-neutral-300 bg-white hover:bg-neutral-50">
              <Share2 className="mr-1.5 h-4 w-4" /> Share
            </Button>
          </div>
        }
      />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <button data-testid="back-btn" onClick={() => artist && navigate(`/artists/${artist.id}`)} className="mb-6 flex items-center gap-1.5 text-sm text-neutral-500 transition-colors hover:text-black">
          <ArrowLeft className="h-4 w-4" /> {artist?.name || "Artist"}
        </button>

        {!data ? (
          <div className="space-y-6"><Skeleton className="h-40 rounded-2xl" /><div className="grid grid-cols-3 gap-6"><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /></div></div>
        ) : (
          <>
            {/* Song rollout header */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="pb-card flex flex-col gap-5 p-5 sm:flex-row sm:items-center">
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-neutral-900">
                {song.cover_art ? <img src={thumbUrl(song.cover_art, 240)} alt={song.title} width={96} height={96} loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-neutral-600"><Disc3 className="h-8 w-8" /></div>}
              </div>
              <div className="shrink-0 self-start" data-testid="song-cover-setter">
                <CoverSetter endpoint={`/songs/${songId}/cover`} onDone={load} testid="song-set-cover" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#6E97A2]">Song rollout</p>
                <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
                  {song.title} <span className="text-neutral-400">— {artist?.name}</span>
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {data.platforms.map((p) => <PlatformBadge key={p} platform={p} />)}
                  <span className="text-sm text-neutral-500">· combined across {campaigns.length} campaign{campaigns.length === 1 ? "" : "s"}</span>
                </div>
              </div>
              <Button data-testid="add-run-btn" onClick={() => setImportOpen(true)} variant="outline" className="shrink-0 self-start rounded-full border-[#35606E]/30 bg-white text-[#35606E] hover:bg-[#35606E]/5 sm:self-center">
                <Link2 className="mr-1.5 h-4 w-4" /> Add campaign run
              </Button>
            </motion.div>

            <div className="mt-6">
              <CampaignReport data={data} hideHeader middleSlot={runsSlot} artistImage={artist?.image} dashZeros={songId === DASH_ZERO_SONG_ID} />
            </div>

            <EditorialPlacements
              className="mt-12"
              placements={song.editorial_placements}
              source={song.editorial_placements_source}
            />

            <PressSection className="mt-12" items={data.press || []} onAdd={() => setPressOpen(true)} onDelete={deletePress} />
          </>
        )}
      </main>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} type="song" id={songId} />
      <ImportLinkDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        artistId={artist?.id}
        songId={songId}
        onImported={() => load()}
      />
      <PressDialog open={pressOpen} onOpenChange={setPressOpen} songId={songId} onAdded={() => load()} />
    </div>
  );
}
